import { useState, useEffect, useRef } from 'react';
import { mobileAndTableCheck } from '../utils/mobileAndTabletCheck/mobileAndTableCheck';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type PWABrowser =
  | 'safari-ios'
  | 'safari-desktop'
  | 'chrome-ios'
  | 'firefox-ios'
  | 'edge-ios'
  | 'chrome'
  | 'edge'
  | 'samsung'
  | 'firefox'
  | 'opera'
  | 'other';

interface PWAInstallHook {
  isInstallable: boolean;
  isInstalled: boolean;
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  browser: PWABrowser;
  browserSupportsInstall: boolean;
  promptInstall: () => Promise<boolean>;
  dismissPrompt: () => void;
  showPromptBanner: boolean;
}

export const detectBrowser = (userAgent: string): PWABrowser => {
  // iPadOS 13+ Safari reports as Macintosh; distinguish it via touch support
  const isIPadOS = /Macintosh/.test(userAgent) && window.navigator.maxTouchPoints > 1;
  const isIOSDevice = (/iPad|iPhone|iPod/.test(userAgent) || isIPadOS) && !(window as any).MSStream;

  if (isIOSDevice) {
    if (/CriOS/.test(userAgent)) return 'chrome-ios';
    if (/FxiOS/.test(userAgent)) return 'firefox-ios';
    if (/EdgiOS/.test(userAgent)) return 'edge-ios';
    if (/Safari/.test(userAgent)) return 'safari-ios';
    return 'other';
  }

  if (/EdgA?\//.test(userAgent)) return 'edge';
  if (/SamsungBrowser/.test(userAgent)) return 'samsung';
  if (/OPR\//.test(userAgent) || /Opera/.test(userAgent)) return 'opera';
  if (/Firefox/.test(userAgent)) return 'firefox';
  if (/Chrome/.test(userAgent)) return 'chrome';
  if (/Macintosh/.test(userAgent) && /Safari/.test(userAgent)) return 'safari-desktop';
  return 'other';
};

const DISMISSED_KEY = 'Tinybots.pwa.installPromptDismissed';
const DISMISSED_COUNT_KEY = 'Tinybots.pwa.dismissCount';
const MAX_DISMISSALS = 2;

export const usePWAInstall = (): PWAInstallHook => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [browser, setBrowser] = useState<PWABrowser>('other');
  const [browserSupportsInstall, setBrowserSupportsInstall] = useState(false);
  const [showPromptBanner, setShowPromptBanner] = useState(false);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const isInstalledRef = useRef(false);

  useEffect(() => {
    const checkIfInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSStandalone = (window.navigator as any).standalone === true;
      isInstalledRef.current = isStandalone || isIOSStandalone;
      setIsInstalled(isStandalone || isIOSStandalone);
    };

    const checkDeviceAndBrowser = () => {
      const userAgent = window.navigator.userAgent || window.navigator.vendor || (window as any).opera;
      setIsMobile(mobileAndTableCheck());
      
      const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
      setIsIOS(isIOSDevice);
      
      const isAndroidDevice = /Android/.test(userAgent);
      setIsAndroid(isAndroidDevice);
      
      // Detect the actual browser so the banner can show browser-appropriate instructions
      const detectedBrowser = detectBrowser(userAgent);
      setBrowser(detectedBrowser);
      
      // Browser supports PWA install (natively or via manual instructions)
      const supportsInstall = detectedBrowser !== 'other';
      setBrowserSupportsInstall(supportsInstall);
    };

    checkIfInstalled();
    checkDeviceAndBrowser();
    
    const shouldShowBanner = () => {
      const dismissedTimestamp = localStorage.getItem(DISMISSED_KEY);
      const dismissCount = parseInt(localStorage.getItem(DISMISSED_COUNT_KEY) || '0', 10);
      
      if (dismissCount >= MAX_DISMISSALS) {
        return false;
      }
      
      if (dismissedTimestamp) {
        const dismissedDate = new Date(dismissedTimestamp);
        const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysSinceDismissed < 7) {
          return false;
        }
      }
      
      return true;
    };

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      const dismissedTimestamp = localStorage.getItem(DISMISSED_KEY);
      const dismissCount = parseInt(localStorage.getItem(DISMISSED_COUNT_KEY) || '0', 10);
      
      if (dismissCount >= MAX_DISMISSALS) {
        return;
      }
      
      if (dismissedTimestamp) {
        const dismissedDate = new Date(dismissedTimestamp);
        const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysSinceDismissed < 7) {
          return;
        }
      }
      
      setTimeout(() => {
        setShowPromptBanner(true);
      }, 3000);
    };

    const handleAppInstalled = () => {
      isInstalledRef.current = true;
      setIsInstalled(true);
      deferredPromptRef.current = null;
      setDeferredPrompt(null);
      setShowPromptBanner(false);
      localStorage.removeItem(DISMISSED_KEY);
      localStorage.removeItem(DISMISSED_COUNT_KEY);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Fallback: If browser supports PWA but beforeinstallprompt hasn't fired after 5 seconds
    // (e.g., Firefox Android, iOS, Safari desktop), show banner with manual instructions
    const fallbackTimer = setTimeout(() => {
      const userAgent = window.navigator.userAgent;
      const detectedBrowser = detectBrowser(userAgent);
      const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
      const isAndroidDevice = /Android/.test(userAgent);
      const isSafariDesktop = detectedBrowser === 'safari-desktop';
      const supportsManualInstall = isIOSDevice || isAndroidDevice || isSafariDesktop;
      
      // Show banner if:
      // 1. The browser supports installing the PWA (natively or manually)
      // 2. App is not already installed
      // 3. beforeinstallprompt hasn't fired (no deferredPrompt)
      // 4. User hasn't dismissed too many times
      if (supportsManualInstall && !isInstalledRef.current && !deferredPromptRef.current && shouldShowBanner()) {
        setShowPromptBanner(true);
      }
    }, 5000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearTimeout(fallbackTimer);
    };
  }, []);

  const promptInstall = async (): Promise<boolean> => {
    if (!deferredPrompt) {
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        deferredPromptRef.current = null;
        setDeferredPrompt(null);
        setShowPromptBanner(false);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error prompting install:', error);
      return false;
    }
  };

  const dismissPrompt = () => {
    setShowPromptBanner(false);
    localStorage.setItem(DISMISSED_KEY, new Date().toISOString());
    
    const currentCount = parseInt(localStorage.getItem(DISMISSED_COUNT_KEY) || '0', 10);
    localStorage.setItem(DISMISSED_COUNT_KEY, String(currentCount + 1));
  };

  return {
    isInstallable: !!deferredPrompt,
    isInstalled,
    isMobile,
    isIOS,
    isAndroid,
    browser,
    browserSupportsInstall,
    promptInstall,
    dismissPrompt,
    showPromptBanner
  };
};
